import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiDel } from '../lib/api.js';
import { fmtFromNow, fmtDate, initials } from '../lib/format.js';
import { useAuth } from '../store/auth.js';
import LeadDrawer from '../components/LeadDrawer.jsx';
import { Inbox as InboxIcon, Send, AlertTriangle, Clock, RefreshCw, Trash2 } from 'lucide-react';

// Sort inbox threads so agents can act on responses first:
//   Tier 1 (pinned): threads with unread inbound  — by most recent inbound desc
//   Tier 2: everything else — by most recent message activity desc
// The old backend returned threads in "whichever tenant message was newest"
// order, which mixed unanswered outbound blasts in with genuine replies.
function sortThreads(threads) {
  return [...threads].sort((a, b) => {
    const aReplied = a.unread > 0;
    const bReplied = b.unread > 0;
    if (aReplied !== bReplied) return aReplied ? -1 : 1;
    return new Date(b.last?.createdAt || 0) - new Date(a.last?.createdAt || 0);
  });
}

export default function Inbox() {
  const [tab, setTab] = useState('inbox');
  return (
    <div className="space-y-4 max-w-[1500px] mx-auto">
      <header>
        <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs">Communications</div>
        <h1 className="font-display text-3xl font-bold mt-1">Inbox</h1>
      </header>

      <div className="flex border-b border-civix-border gap-1">
        {[
          ['inbox', 'Inbox', InboxIcon],
          ['sent', 'Sent', Send],
          ['rejected', 'Rejected', AlertTriangle],
          ['scheduled', 'Scheduled', Clock],
        ].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm border-b-2 flex items-center gap-1.5 transition ${
              tab === k ? 'border-civix-purple text-white' : 'border-transparent text-white/50 hover:text-white'
            }`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {tab === 'inbox' && <InboxThreads />}
      {tab === 'sent' && <SentList />}
      {tab === 'rejected' && <RejectedList />}
      {tab === 'scheduled' && <ScheduledList />}
    </div>
  );
}

function InboxThreads() {
  // Default scope is 'mine' — admins can flip to 'agency' to see the whole
  // tenant. Agents can request 'agency' but the API silently downgrades them,
  // so we hide the toggle for non-privileged roles.
  const { agent } = useAuth();
  // Only admin/superadmin can flip to the agency-wide view. Managers don't have
  // a distinct role in the codebase; agents always see 'mine' only.
  const isPrivileged = agent?.role === 'admin' || agent?.role === 'superadmin';
  const [scope, setScope] = useState('mine');
  const effectiveScope = isPrivileged ? scope : 'mine';
  const { data, refetch } = useQuery({
    queryKey: ['inbox', effectiveScope],
    queryFn: () => apiGet(`/sms/inbox?scope=${effectiveScope}`),
    refetchInterval: 5000,
  });
  const [params, setParams] = useSearchParams();
  const open = params.get('open');
  const rawThreads = data?.threads || [];
  const threads = useMemo(() => sortThreads(rawThreads), [rawThreads]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="panel divide-y divide-civix-border/50 max-h-[80vh] overflow-y-auto">
        {isPrivileged && (
          <div className="flex items-center gap-1 p-2 border-b border-civix-border/50 sticky top-0 bg-civix-panel/95 backdrop-blur-sm z-10">
            {[['mine', 'My inbox'], ['agency', 'Agency inbox']].map(([k, label]) => (
              <button key={k} onClick={() => setScope(k)}
                className={`px-3 py-1 text-xs rounded-md transition ${
                  scope === k ? 'bg-civix-purple/30 text-white border border-civix-purple/60' : 'text-white/50 hover:text-white'
                }`}>{label}</button>
            ))}
          </div>
        )}
        {threads.length === 0 && <div className="p-6 text-white/50 text-sm">No conversations yet.</div>}
        {threads.map((t) => (
          <button key={t.lead._id} onClick={() => setParams({ open: t.lead._id })}
            className={`w-full text-left p-4 flex gap-3 items-start hover:bg-civix-panelLight/50 transition ${open === t.lead._id ? 'bg-civix-panelLight/70' : ''}`}>
            <div className="w-9 h-9 rounded-full bg-civix-gradient flex items-center justify-center text-xs font-bold shrink-0">
              {initials(t.lead.firstName + ' ' + (t.lead.lastName || ''))}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium truncate flex items-center gap-2">
                  <span className="truncate">{t.lead.firstName} {t.lead.lastName}</span>
                  {t.unread > 0 && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-civix-cyan/20 text-civix-cyan shrink-0">
                      Reply
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/40 shrink-0">{fmtFromNow(t.last.createdAt)}</div>
              </div>
              <div className="text-sm text-white/60 truncate">{t.last.body}</div>
            </div>
            {t.unread > 0 && <div className="w-2 h-2 rounded-full bg-civix-cyan mt-2" />}
          </button>
        ))}
      </div>
      <div className="hidden md:block">
        {open ? null : (
          <div className="panel p-10 text-center text-white/50 h-full flex items-center justify-center">
            Select a conversation to view the thread.
          </div>
        )}
      </div>
      {open && <LeadDrawer leadId={open} onClose={() => setParams({})} onChange={refetch} />}
    </div>
  );
}

function SentList() {
  const { data } = useQuery({ queryKey: ['sms-sent'], queryFn: () => apiGet('/sms/sent'), refetchInterval: 8000 });
  const items = data?.items || [];
  return (
    <div className="panel divide-y divide-civix-border/50 max-h-[80vh] overflow-y-auto">
      {items.length === 0 && <div className="p-6 text-white/50 text-sm">No sent messages yet.</div>}
      {items.map((m) => (
        <div key={m._id} className="p-4">
          <div className="flex items-center justify-between text-xs text-white/50">
            <span>{m.lead?.firstName} {m.lead?.lastName} · {m.lead?.phone}</span>
            <span className="flex items-center gap-2">
              <span className={`pill text-[10px] ${m.status === 'delivered' ? 'pill-purple' : 'pill-cold'}`}>{m.status}</span>
              <span>{fmtDate(m.createdAt)}</span>
            </span>
          </div>
          <div className="text-sm text-white/85 mt-1">{m.body}</div>
        </div>
      ))}
    </div>
  );
}

function RejectedList() {
  const { data, refetch } = useQuery({ queryKey: ['sms-rejected'], queryFn: () => apiGet('/sms/rejected'), refetchInterval: 8000 });
  const items = data?.items || [];

  async function resend(id) {
    try { await apiPost(`/sms/resend/${id}`); toast.success('Resent'); refetch(); }
    catch (e) { toast.error(e.response?.data?.error || 'Resend failed'); }
  }

  return (
    <div className="panel divide-y divide-civix-border/50 max-h-[80vh] overflow-y-auto">
      {items.length === 0 && <div className="p-6 text-white/50 text-sm">No rejected messages.</div>}
      {items.map((m) => (
        <div key={m._id} className="p-4">
          <div className="flex items-center justify-between text-xs text-white/50">
            <span>{m.lead?.firstName} {m.lead?.lastName} · {m.lead?.phone}</span>
            <span className="flex items-center gap-2">
              <span className="pill-hot text-[10px]">{m.status}</span>
              {m.errorCode && <span className="text-red-300/80">code {m.errorCode}</span>}
              <span>{fmtDate(m.createdAt)}</span>
            </span>
          </div>
          <div className="text-sm text-white/85 mt-1">{m.body}</div>
          {m.errorMessage && <div className="text-xs text-red-300/70 mt-1">{m.errorMessage}</div>}
          <div className="mt-2">
            <button onClick={() => resend(m._id)} className="btn-ghost text-xs flex items-center gap-1.5">
              <RefreshCw className="w-3 h-3" />Resend
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ScheduledList() {
  const { data, refetch } = useQuery({ queryKey: ['sms-scheduled'], queryFn: () => apiGet('/sms/scheduled'), refetchInterval: 8000 });
  const items = data?.items || [];

  async function cancel(id) {
    try { await apiDel(`/sms/scheduled/${id}`); toast.success('Cancelled'); refetch(); }
    catch (e) { toast.error(e.response?.data?.error || 'Cancel failed'); }
  }

  return (
    <div className="panel divide-y divide-civix-border/50 max-h-[80vh] overflow-y-auto">
      {items.length === 0 && <div className="p-6 text-white/50 text-sm">No texts scheduled.</div>}
      {items.map((m) => (
        <div key={m._id} className="p-4">
          <div className="flex items-center justify-between text-xs text-white/50">
            <span>{m.lead?.firstName} {m.lead?.lastName} · {m.lead?.phone}</span>
            <span className="flex items-center gap-2">
              <span className="pill-purple text-[10px]">fires {fmtDate(m.scheduledFor)}</span>
            </span>
          </div>
          <div className="text-sm text-white/85 mt-1">{m.body}</div>
          <div className="mt-2">
            <button onClick={() => cancel(m._id)} className="btn-ghost text-xs flex items-center gap-1.5 hover:border-red-500/60! hover:text-red-300!">
              <Trash2 className="w-3 h-3" />Cancel
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
